import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import type IProduct from 'interfaces/IProduct';

import type { WizardLoading, WizardStateValue } from './types';
import { DEFAULT_LOADING } from './types';

const WizardStateContext = createContext<WizardStateValue | null>(null);

export const WizardStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [step, setStep] = useState(1);
  const [selected, setSelectedState] = useState(0);
  const [loading, setLoading] = useState<WizardLoading>({ ...DEFAULT_LOADING });
  const [product, setProductState] = useState<IProduct | null>(null);

  const nextStep = useCallback(() => setStep(s => s + 1), []);
  const previousStep = useCallback(() => setStep(s => s - 1), []);
  const resetWizard = useCallback((initialStep = 1) => setStep(initialStep), []);

  const setSelected = useCallback((value: number) => setSelectedState(value), []);
  const resetSelected = useCallback((initialSelected = 1) => setSelectedState(initialSelected), []);

  const updateLoading = useCallback((percent: number) => setLoading({ active: true, percent }), []);
  const resetLoading = useCallback(() => setLoading({ ...DEFAULT_LOADING }), []);

  const setProduct = useCallback((p: IProduct) => setProductState(p), []);
  const resetProduct = useCallback(() => setProductState(null), []);

  const value = useMemo<WizardStateValue>(
    () => ({
      step,
      selected,
      loading,
      product,
      nextStep,
      previousStep,
      resetWizard,
      setSelected,
      resetSelected,
      updateLoading,
      resetLoading,
      setProduct,
      resetProduct
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [step, selected, loading, product]
  );

  return <WizardStateContext.Provider value={value}>{children}</WizardStateContext.Provider>;
};

export function useWizardState(): WizardStateValue {
  const ctx = useContext(WizardStateContext);

  if (!ctx) {
    throw new Error('useWizardState must be used within a WizardStateProvider');
  }

  return ctx;
}
